import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common'
import { Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonColor, ButtonVariant, Modal } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { EVENT_KEYS } from '@/shared/custom-event'

import { useGridV2Context } from '../../contexts/GridV2Context'

interface IDependentColumns {
  connections: Set<string> // connection column id
  lookups: Set<string> // lookup column id
}

function checkColumnValueInTables(tables: TableAdapter[], columnId: string): Map<string, IDependentColumns> {
  const matchingColumns: Map<string, IDependentColumns> = new Map()

  tables.forEach((table) => {
    const dependentColumnIdsInTable = new Set<string>()

    // First, find all columns that directly depend on the column being deleted.
    table.columns.forEach((column) => {
      const { connections } = column
      if (connections?.fColId === columnId) {
        dependentColumnIdsInTable.add(column.id)
      }
    })

    // Iteratively find columns that depend on the already identified dependent columns.
    let newDependenciesFound = true
    while (newDependenciesFound) {
      newDependenciesFound = false
      table.columns.forEach((column) => {
        if (dependentColumnIdsInTable.has(column.id)) {
          return // Skip columns that are already marked as dependent
        }

        const { lookup } = column
        if (lookup?.connColId && dependentColumnIdsInTable.has(lookup.connColId)) {
          dependentColumnIdsInTable.add(column.id)
          newDependenciesFound = true
        }
      })
    }

    if (dependentColumnIdsInTable.size > 0) {
      const columnIdToDataMap = new Map<string, { id: string; name: string; type: string }>()
      table.columns.forEach((col) => columnIdToDataMap.set(col.id, { id: col.id, name: col.name, type: col.type }))

      const dependentColumns: IDependentColumns = {
        connections: new Set<string>(),
        lookups: new Set<string>(),
      }

      dependentColumnIdsInTable.forEach((id) => {
        const columnData = columnIdToDataMap.get(id)
        if (columnData) {
          if (columnData.type === 'connection') {
            dependentColumns.connections.add(columnData.id)
          } else if (columnData.type === 'lookup') {
            dependentColumns.lookups.add(columnData.id)
          }
        }
      })

      if (dependentColumns.connections.size > 0 || dependentColumns.lookups.size > 0) {
        matchingColumns.set(table.id, dependentColumns)
      }
    }
  })

  return matchingColumns
}

interface IGridDeleteModal {
  column: ColumnAdapter | undefined
}

export function GridDeleteModal({ column }: IGridDeleteModal) {
  const { t } = useTranslation()

  const { tablesMap } = useDbClientContext()
  const {
    handleDeleteColumn,
    table,
    tableList,
    isLoading,
    handleCloseMenu,
    handleDeleteMultipleColumns,
    selection,
    glideColumns,
  } = useGridV2Context()

  const [dependents, setDependents] = useState<Map<string, IDependentColumns>>(new Map())
  const [loading, setLoading] = useState(false)

  const selectedColumnIds = (selection?.columns?.toArray?.() || [])
    .map((idx: number) => glideColumns?.[idx]?.id)
    .filter((id: unknown): id is string => typeof id === 'string' && id !== '_id')
  const isMultiColumns = selectedColumnIds.length > 1

  useEffect(() => {
    if (!column || !table || !tableList.length) return
    // find dependents
    setDependents(checkColumnValueInTables(tableList, column?.id))

    return () => {
      setDependents(new Map())
    }
  }, [column, table, tableList])

  const handleDependentColumnsDelete = async () => {
    if (!dependents.size || !table || !column) return

    setLoading(true)
    await handleDeleteMultipleColumns(dependents)
    await handleDeleteColumn(table.id, column?.id as string)
    setLoading(false)
    window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_CLIENT_RELOAD))
    handleCloseMenu()
    toast.success(t('Columns deleted'))
  }

  const handleDelete = async () => {
    if (!column || !table) return

    setLoading(true)
    await handleDeleteColumn(table.id, column?.id as string)
    setLoading(false)
    handleCloseMenu()
    toast.success(t('Column deleted'))
  }

  const handleDeleteSelected = async () => {
    if (!table || selectedColumnIds.length === 0) return

    setLoading(true)
    await Promise.allSettled(selectedColumnIds.map((id) => handleDeleteColumn(table.id, id)))
    setLoading(false)
    handleCloseMenu()
    toast.success(t('{{count}} columns deleted', { count: selectedColumnIds.length }))
  }

  return (
    <div className='w-[360px] md:w-[580px] lg:w-[550px] mk-bg text-m-m'>
      <Modal.Header onCloseClick={handleCloseMenu}>
        <Trans>{isMultiColumns ? 'Delete Columns' : 'Delete Column'}</Trans>
      </Modal.Header>

      <div className='p-4 py-6 pb-8'>
        {isMultiColumns ? (
          <Trans values={{ count: selectedColumnIds.length }}>
            {t('Delete Columns Confirm', {
              count: selectedColumnIds.length,
            })}
          </Trans>
        ) : dependents?.size > 0 ? (
          <>
            <p>
              <Trans>
                <strong>Warning:</strong> Deleting this column will also <strong>permanently delete</strong> the
                following columns and all their data. This action cannot be undone.
              </Trans>
            </p>

            <table className='mt-4 w-full border bdr rounded-sm table-fixed text-m-s'>
              <thead>
                <tr className='border-b bdr bg-zinc-100 dark:bg-slate-900'>
                  <th className='text-left px-3 py-2 font-medium w-1/4'>Table</th>
                  <th className='text-left px-3 py-2 font-medium w-1/4'>Connection</th>
                  <th className='text-left px-3 py-2 font-medium w-1/2'>Lookups</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(dependents).map(([tableId, dependentColumns]) => {
                  const dbTable = tablesMap.get(tableId)
                  return (
                    <tr key={tableId} className='border-b bdr last:border-b-0'>
                      <td className='px-3 py-2'>{dbTable?.settings._Name || 'Unknown Table'}</td>
                      <td className='px-3 py-2 bg-blue-100 dark:bg-blue-900/50'>
                        {Array.from(dependentColumns.connections)
                          .map((id) => dbTable?.columnsMap.get(id)?._Name)
                          .filter(Boolean)
                          .map((name) => (
                            <div key={name}>{name}</div>
                          ))}
                      </td>
                      <td className='px-3 py-2 bg-purple-100 dark:bg-purple-900/50'>
                        {Array.from(dependentColumns.lookups)
                          .map((id) => dbTable?.columnsMap.get(id)?._Name)
                          .filter(Boolean)
                          .map((name) => (
                            <div key={name}>&bull; {name}</div>
                          ))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        ) : (
          <Trans values={{ colName: column?.name }}>
            Are you sure you&apos;d like to delete <strong>{column?.name}</strong> column?
          </Trans>
        )}
      </div>

      <Modal.Footer className='justify-between'>
        <Button onClick={handleCloseMenu} variant={ButtonVariant.Outlined} colorStyle={ButtonColor.Secondary}>
          <Trans>Cancel</Trans>
        </Button>

        {isMultiColumns ? (
          <Button
            icon={isLoading ? <span className='loading' /> : <Trash16 className='shrink-0' />}
            colorStyle={ButtonColor.Secondary}
            onClick={handleDeleteSelected}
            disabled={loading}
          >
            {loading ? <span className='loading spin' /> : <Trans>Delete Selected Columns</Trans>}
          </Button>
        ) : dependents?.size > 0 ? (
          <Button
            icon={isLoading ? <span className='loading' /> : <Trash16 className='shrink-0' />}
            colorStyle={ButtonColor.Secondary}
            onClick={handleDependentColumnsDelete}
            disabled={loading}
          >
            {loading ? <span className='loading spin' /> : <Trans>Delete All Columns</Trans>}
          </Button>
        ) : (
          <Button
            icon={isLoading ? <span className='loading' /> : <Trash16 className='shrink-0' />}
            colorStyle={ButtonColor.Secondary}
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? <span className='loading spin' /> : <Trans>Yes, delete</Trans>}
          </Button>
        )}
      </Modal.Footer>
    </div>
  )
}
